import React, { useState, useEffect } from 'react';
import { supabase } from '../../utils/supabaseClient';
import '../../styles/SystemProperties.css';

function SystemProperties() {
  const [properties, setProperties] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingRow, setEditingRow] = useState(null);
  const [editKey, setEditKey] = useState('');
  const [editValue, setEditValue] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const [searchTerm, setSearchTerm] = useState('');

  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type: 'success' }), 3000);
  };

  const fetchProperties = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { data, error } = await supabase
        .from('system_properties')
        .select('*')
        .order('key', { ascending: true });

      if (error) throw error;
      setProperties(data || []);
    } catch (err) {
      console.error('Error fetching system properties:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchProperties();
  }, []);

  const handleAdd = async () => {
    if (!newKey.trim()) {
      showNotification('Key is required', 'error');
      return;
    }
    // Check for duplicate key
    if (properties.some(p => p.key === newKey.trim())) {
      showNotification('This key already exists', 'error');
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase
        .from('system_properties')
        .insert([{ key: newKey.trim(), value: newValue.trim() }]);

      if (error) throw error;
      showNotification('Property added successfully');
      setNewKey('');
      setNewValue('');
      setIsAdding(false);
      fetchProperties();
    } catch (err) {
      console.error('Error adding property:', err);
      showNotification(`Failed to add: ${err.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (property) => {
    setEditingRow(property.id);
    setEditKey(property.key);
    setEditValue(property.value || '');
  };

  const handleSaveEdit = async () => {
    if (!editKey.trim()) {
      showNotification('Key is required', 'error');
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase
        .from('system_properties')
        .update({ key: editKey.trim(), value: editValue.trim() })
        .eq('id', editingRow);

      if (error) throw error;
      showNotification('Property updated successfully');
      setEditingRow(null);
      fetchProperties();
    } catch (err) {
      console.error('Error updating property:', err);
      showNotification(`Failed to update: ${err.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, key) => {
    if (!window.confirm(`Delete property "${key}"?`)) return;

    try {
      const { error } = await supabase
        .from('system_properties')
        .delete()
        .eq('id', id);

      if (error) throw error;
      showNotification('Property deleted');
      fetchProperties();
    } catch (err) {
      console.error('Error deleting property:', err);
      showNotification(`Failed to delete: ${err.message}`, 'error');
    }
  };

  const filteredProperties = properties.filter(p =>
    p.key.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (p.value || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="system-properties-container">
      {notification.show && (
        <div className={`sp-notification ${notification.type}`}>
          <i className={`fas ${notification.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'}`}></i>
          {notification.message}
        </div>
      )}

      <div className="sp-header">
        <div className="sp-title-section">
          <h1><i className="fas fa-sliders-h"></i> System Properties</h1>
          <p className="sp-subtitle">Manage key-value configuration for the system</p>
        </div>
        <div className="sp-actions">
          <div className="sp-search">
            <i className="fas fa-search"></i>
            <input
              type="text"
              placeholder="Search properties..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button className="sp-add-btn" onClick={() => { setIsAdding(true); setNewKey(''); setNewValue(''); }}>
            <i className="fas fa-plus"></i> Add Property
          </button>
        </div>
      </div>

      {error && (
        <div className="sp-error">
          <i className="fas fa-exclamation-triangle"></i> {error}
          <button onClick={fetchProperties}><i className="fas fa-sync"></i> Retry</button>
        </div>
      )}

      <div className="sp-table-wrapper">
        <table className="sp-table">
          <thead>
            <tr>
              <th style={{ width: '60px' }}>#</th>
              <th>Key</th>
              <th>Value</th>
              <th style={{ width: '120px' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {isAdding && (
              <tr className="sp-adding-row">
                <td><i className="fas fa-plus-circle" style={{ color: '#22c55e' }}></i></td>
                <td>
                  <input
                    type="text"
                    className="sp-input"
                    placeholder="Enter key"
                    value={newKey}
                    onChange={(e) => setNewKey(e.target.value)}
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleAdd();
                      if (e.key === 'Escape') setIsAdding(false);
                    }}
                  />
                </td>
                <td>
                  <input
                    type="text"
                    className="sp-input"
                    placeholder="Enter value"
                    value={newValue}
                    onChange={(e) => setNewValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleAdd();
                      if (e.key === 'Escape') setIsAdding(false);
                    }}
                  />
                </td>
                <td>
                  <div className="sp-action-buttons">
                    <button className="sp-save-btn" onClick={handleAdd} disabled={saving}>
                      <i className={`fas ${saving ? 'fa-spinner fa-spin' : 'fa-check'}`}></i>
                    </button>
                    <button className="sp-cancel-btn" onClick={() => setIsAdding(false)}>
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                </td>
              </tr>
            )}
            {isLoading ? (
              <tr>
                <td colSpan="4" className="sp-loading">
                  <i className="fas fa-spinner fa-spin"></i> Loading properties...
                </td>
              </tr>
            ) : filteredProperties.length === 0 ? (
              <tr>
                <td colSpan="4" className="sp-empty">
                  <i className="fas fa-inbox"></i>
                  <p>{searchTerm ? 'No properties match your search' : 'No properties configured yet'}</p>
                </td>
              </tr>
            ) : (
              filteredProperties.map((prop, index) => (
                <tr key={prop.id}>
                  <td className="sp-index">{index + 1}</td>
                  {editingRow === prop.id ? (
                    <>
                      <td>
                        <input
                          type="text"
                          className="sp-input"
                          value={editKey}
                          onChange={(e) => setEditKey(e.target.value)}
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveEdit();
                            if (e.key === 'Escape') setEditingRow(null);
                          }}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          className="sp-input"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveEdit();
                            if (e.key === 'Escape') setEditingRow(null);
                          }}
                        />
                      </td>
                      <td>
                        <div className="sp-action-buttons">
                          <button className="sp-save-btn" onClick={handleSaveEdit} disabled={saving}>
                            <i className={`fas ${saving ? 'fa-spinner fa-spin' : 'fa-check'}`}></i>
                          </button>
                          <button className="sp-cancel-btn" onClick={() => setEditingRow(null)}>
                            <i className="fas fa-times"></i>
                          </button>
                        </div>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="sp-key">{prop.key}</td>
                      <td className="sp-value">{prop.value || <span className="sp-null">—</span>}</td>
                      <td>
                        <div className="sp-action-buttons">
                          <button className="sp-edit-btn" onClick={() => handleEdit(prop)} title="Edit">
                            <i className="fas fa-pen"></i>
                          </button>
                          <button className="sp-delete-btn" onClick={() => handleDelete(prop.id, prop.key)} title="Delete">
                            <i className="fas fa-trash-alt"></i>
                          </button>
                        </div>
                      </td>
                    </>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="sp-footer">
        <span>{filteredProperties.length} {filteredProperties.length === 1 ? 'property' : 'properties'}</span>
      </div>
    </div>
  );
}

export default SystemProperties;
